import { create } from 'zustand';
import type { SessionPlan, SessionScene, ChaseTrack, OptionalEncounter, DbSessionPlan } from '@machina/core';
import { supabase } from '../lib/supabase';
import { persistWithRetry } from '../lib/persistence';
import { useCampaignStore } from './campaignStore';

/**
 * Session Plan Store
 *
 * Manages one-off session plans for the GM.
 * Sessions are campaign-scoped side quests, solo adventures, or encounter packs
 * that plug into the campaign's lore and progression.
 */

interface SessionPlanState {
  // All sessions for the current campaign
  sessions: Map<string, SessionPlan>;
  activeSessionId: string | null;

  // Set when a debounced save exhausted its retries — surfaces "unsaved
  // changes" so a stuck write is never silent.
  dirty: boolean;

  // Actions
  addSession: (session: SessionPlan) => void;
  removeSession: (id: string) => void;
  resetSession: (id: string) => void;
  setActiveSession: (id: string | null) => void;

  // Scene management
  advanceScene: () => void;
  setSceneStatus: (sceneId: string, status: SessionScene['status']) => void;
  updateScene: (sceneId: string, updates: Partial<SessionScene>) => void;

  // Skill checks
  resolveCheck: (sceneId: string, checkId: string, passed: boolean) => void;

  // Clue cards
  discoverClue: (clueId: string) => void;

  // Chase
  chaseProgress: (sceneId: string, amount: number) => void;
  chaseFail: (sceneId: string) => void;
  chaseReset: (sceneId: string) => void;

  // Optional encounters
  updateOptionalEncounter: (encounterId: string, updates: Partial<OptionalEncounter>) => void;

  // DB persistence
  dbPlanIds: Map<string, string>;  // local session plan id → DB UUID
  saveToDb: (sessionId: string, campaignId: string) => Promise<string | null>;
  loadFromDb: (campaignId: string) => Promise<void>;

  // Module content injection
  injectModuleSessions: (sessions: SessionPlan[]) => void;
  removeModuleSessions: (moduleNamespace: string) => void;

  // Queries
  getActiveSession: () => SessionPlan | undefined;
  getActiveScene: () => SessionScene | undefined;
}

// =============================================================================
// DEBOUNCED PERSISTENCE — keep live session-plan progress saved
// =============================================================================
//
// saveToDb() (below) is only invoked on session completion, so per-action
// progress (scene advances, resolved checks, discovered clues, chase
// progress, etc.) used to live only in memory and was lost on reload. We
// mirror the encounterStore / mobStore pattern: every progress-mutating action
// schedules a debounced save (~1s, keyed by sessionId) that runs through the
// shared persistWithRetry helper so a failed save is NEVER silent.

const SAVE_DEBOUNCE_MS = 1000;
const _saveTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Persist the given session plan to Supabase with retry + sticky failure toast.
 * Returns the DB UUID on success (creating the row on first save), or null on
 * failure. Wrapped in persistWithRetry so writes never fail silently.
 */
async function persistSessionPlan(sessionId: string, campaignId: string): Promise<string | null> {
  const store = useSessionPlanStore.getState();
  const session = store.sessions.get(sessionId);
  if (!session) return null;

  const existingDbId = store.dbPlanIds.get(sessionId);
  const row = {
    campaign_id: campaignId,
    name: session.name,
    status: session.status,
    plan_data: JSON.parse(JSON.stringify(session)),
    updated_at: new Date().toISOString(),
  };

  let dbId: string | null = null;

  const { ok } = await persistWithRetry(
    'session progress',
    async () => {
      if (existingDbId) {
        const res = await supabase
          .from('machina_session_plans')
          .update(row)
          .eq('id', existingDbId)
          .select()
          .single();
        if (!res.error && res.data) dbId = (res.data as unknown as DbSessionPlan).id;
        return res;
      }
      const res = await supabase
        .from('machina_session_plans')
        .insert(row)
        .select()
        .single();
      if (!res.error && res.data) dbId = (res.data as unknown as DbSessionPlan).id;
      return res;
    },
    { onFinalFailure: () => useSessionPlanStore.setState({ dirty: true }) },
  );

  if (ok && dbId) {
    useSessionPlanStore.setState((state) => {
      const newDbPlanIds = new Map(state.dbPlanIds);
      newDbPlanIds.set(sessionId, dbId!);
      return { dbPlanIds: newDbPlanIds, dirty: false };
    });
  }

  return ok ? dbId : null;
}

/**
 * Schedule a debounced save of the active session's progress. No-ops when there
 * is no active session or no current campaign (e.g. module-injected sessions
 * before a campaign is selected). Keyed by sessionId so concurrent sessions
 * each get their own debounce window.
 */
function scheduleSessionSave(): void {
  const { activeSessionId } = useSessionPlanStore.getState();
  if (!activeSessionId) return;

  const campaignId = useCampaignStore.getState().currentCampaignId;
  if (!campaignId) return;

  const existing = _saveTimers.get(activeSessionId);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(() => {
    _saveTimers.delete(activeSessionId);
    void persistSessionPlan(activeSessionId, campaignId);
  }, SAVE_DEBOUNCE_MS);

  _saveTimers.set(activeSessionId, timer);
}

export const useSessionPlanStore = create<SessionPlanState>((set, get) => ({
  sessions: new Map(),
  activeSessionId: null,
  dbPlanIds: new Map(),
  dirty: false,

  addSession: (session) => {
    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, session);
      return { sessions: newSessions };
    });
  },

  removeSession: (id) => {
    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.delete(id);
      return {
        sessions: newSessions,
        activeSessionId: state.activeSessionId === id ? null : state.activeSessionId,
      };
    });
  },

  resetSession: (id) => {
    set((state) => {
      const session = state.sessions.get(id);
      if (!session) return state;

      const reset: SessionPlan = {
        ...session,
        activeSceneIndex: 0,
        status: 'prep',
        updatedAt: new Date(),
        scenes: session.scenes.map((scene) => ({
          ...scene,
          status: 'upcoming' as const,
          skillChecks: scene.skillChecks.map((check) => ({
            ...check,
            resolved: false,
            passed: false,
          })),
          chase: scene.chase
            ? { ...scene.chase, currentProgress: 0, currentFailures: 0 }
            : undefined,
        })),
        clueCards: session.clueCards.map((clue) => ({
          ...clue,
          discovered: false,
        })),
        optionalEncounters: session.optionalEncounters?.map((enc) => ({
          ...enc,
          used: false,
        })),
      };

      const newSessions = new Map(state.sessions);
      newSessions.set(id, reset);
      return { sessions: newSessions };
    });
  },

  setActiveSession: (id) => set({ activeSessionId: id }),

  advanceScene: () => {
    const session = get().getActiveSession();
    if (!session) return;

    const currentIdx = session.activeSceneIndex;
    const scenes = [...session.scenes];

    // Mark current scene completed
    if (scenes[currentIdx]) {
      scenes[currentIdx] = { ...scenes[currentIdx], status: 'completed' };
    }

    // Advance to next scene
    const nextIdx = currentIdx + 1;
    if (nextIdx < scenes.length) {
      scenes[nextIdx] = { ...scenes[nextIdx], status: 'active' };
    }

    const updated: SessionPlan = {
      ...session,
      scenes,
      activeSceneIndex: Math.min(nextIdx, scenes.length - 1),
      status: nextIdx >= scenes.length ? 'completed' : 'running',
      updatedAt: new Date(),
    };

    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, updated);
      return { sessions: newSessions };
    });
    scheduleSessionSave();
  },

  setSceneStatus: (sceneId, status) => {
    const session = get().getActiveSession();
    if (!session) return;

    const scenes = session.scenes.map((s) =>
      s.id === sceneId ? { ...s, status } : s
    );
    const activeIdx = status === 'active'
      ? scenes.findIndex((s) => s.id === sceneId)
      : session.activeSceneIndex;

    const updated: SessionPlan = {
      ...session,
      scenes,
      activeSceneIndex: activeIdx >= 0 ? activeIdx : session.activeSceneIndex,
      status: 'running',
      updatedAt: new Date(),
    };

    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, updated);
      return { sessions: newSessions };
    });
    scheduleSessionSave();
  },

  updateScene: (sceneId, updates) => {
    const session = get().getActiveSession();
    if (!session) return;

    const scenes = session.scenes.map((s) =>
      s.id === sceneId ? { ...s, ...updates } : s
    );

    const updated: SessionPlan = {
      ...session,
      scenes,
      updatedAt: new Date(),
    };

    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, updated);
      return { sessions: newSessions };
    });
    scheduleSessionSave();
  },

  resolveCheck: (sceneId, checkId, passed) => {
    const session = get().getActiveSession();
    if (!session) return;

    const scenes = session.scenes.map((scene) => {
      if (scene.id !== sceneId) return scene;
      return {
        ...scene,
        skillChecks: scene.skillChecks.map((check) =>
          check.id === checkId ? { ...check, resolved: true, passed } : check
        ),
      };
    });

    const updated: SessionPlan = { ...session, scenes, updatedAt: new Date() };
    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, updated);
      return { sessions: newSessions };
    });
    scheduleSessionSave();
  },

  discoverClue: (clueId) => {
    const session = get().getActiveSession();
    if (!session) return;

    const clueCards = session.clueCards.map((c) =>
      c.id === clueId ? { ...c, discovered: true } : c
    );

    const updated: SessionPlan = { ...session, clueCards, updatedAt: new Date() };
    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, updated);
      return { sessions: newSessions };
    });
    scheduleSessionSave();
  },

  chaseProgress: (sceneId, amount = 1) => {
    const session = get().getActiveSession();
    if (!session) return;

    const scenes = session.scenes.map((scene) => {
      if (scene.id !== sceneId || !scene.chase) return scene;
      const chase: ChaseTrack = {
        ...scene.chase,
        currentProgress: Math.min(scene.chase.progressNeeded, scene.chase.currentProgress + amount),
      };
      return { ...scene, chase };
    });

    const updated: SessionPlan = { ...session, scenes, updatedAt: new Date() };
    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, updated);
      return { sessions: newSessions };
    });
    scheduleSessionSave();
  },

  chaseFail: (sceneId) => {
    const session = get().getActiveSession();
    if (!session) return;

    const scenes = session.scenes.map((scene) => {
      if (scene.id !== sceneId || !scene.chase) return scene;
      const chase: ChaseTrack = {
        ...scene.chase,
        currentFailures: scene.chase.currentFailures + 1,
      };
      return { ...scene, chase };
    });

    const updated: SessionPlan = { ...session, scenes, updatedAt: new Date() };
    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, updated);
      return { sessions: newSessions };
    });
    scheduleSessionSave();
  },

  chaseReset: (sceneId) => {
    const session = get().getActiveSession();
    if (!session) return;

    const scenes = session.scenes.map((scene) => {
      if (scene.id !== sceneId || !scene.chase) return scene;
      const chase: ChaseTrack = {
        ...scene.chase,
        currentProgress: 0,
        currentFailures: 0,
      };
      return { ...scene, chase };
    });

    const updated: SessionPlan = { ...session, scenes, updatedAt: new Date() };
    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, updated);
      return { sessions: newSessions };
    });
    scheduleSessionSave();
  },

  updateOptionalEncounter: (encounterId, updates) => {
    const session = get().getActiveSession();
    if (!session || !session.optionalEncounters) return;

    const optionalEncounters = session.optionalEncounters.map((enc) =>
      enc.id === encounterId ? { ...enc, ...updates } : enc
    );

    const updated: SessionPlan = { ...session, optionalEncounters, updatedAt: new Date() };
    set((state) => {
      const newSessions = new Map(state.sessions);
      newSessions.set(session.id, updated);
      return { sessions: newSessions };
    });
    scheduleSessionSave();
  },

  saveToDb: async (sessionId, campaignId) => {
    const session = get().sessions.get(sessionId);
    if (!session) return null;

    const existingDbId = get().dbPlanIds.get(sessionId);
    const row = {
      campaign_id: campaignId,
      name: session.name,
      status: session.status,
      plan_data: JSON.parse(JSON.stringify(session)),
      updated_at: new Date().toISOString(),
    };

    let dbId: string | null = null;
    if (existingDbId) {
      const { data, error } = await supabase
        .from('machina_session_plans')
        .update(row)
        .eq('id', existingDbId)
        .select()
        .single();
      if (error) {
        console.warn('[SessionPlanStore] Failed to update session plan:', error);
      } else {
        dbId = (data as unknown as DbSessionPlan).id;
        console.log('[SessionPlanStore] Updated session plan:', dbId);
      }
    } else {
      const { data, error } = await supabase
        .from('machina_session_plans')
        .insert(row)
        .select()
        .single();
      if (error) {
        console.warn('[SessionPlanStore] Failed to insert session plan:', error);
      } else {
        dbId = (data as unknown as DbSessionPlan).id;
        console.log('[SessionPlanStore] Inserted session plan:', dbId);
      }
    }

    if (dbId) {
      set((state) => {
        const newDbPlanIds = new Map(state.dbPlanIds);
        newDbPlanIds.set(sessionId, dbId!);
        return { dbPlanIds: newDbPlanIds, dirty: false };
      });
    }

    return dbId;
  },

  loadFromDb: async (campaignId) => {
    const { data, error } = await supabase
      .from('machina_session_plans')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.warn('[SessionPlanStore] Failed to load session plans:', error);
      return;
    }

    const plans = (data ?? []) as unknown as DbSessionPlan[];
    const newSessions = new Map(get().sessions);
    const newDbPlanIds = new Map(get().dbPlanIds);

    for (const plan of plans) {
      const session = plan.plan_data as unknown as SessionPlan;
      newSessions.set(session.id, session);
      newDbPlanIds.set(session.id, plan.id);
    }

    set({ sessions: newSessions, dbPlanIds: newDbPlanIds });
    console.log('[SessionPlanStore] Loaded', plans.length, 'session plans from DB');
  },

  injectModuleSessions: (sessions) => {
    set((state) => {
      const newSessions = new Map(state.sessions);
      for (const session of sessions) {
        newSessions.set(session.id, session);
      }
      return { sessions: newSessions };
    });
  },

  removeModuleSessions: (moduleNamespace) => {
    set((state) => {
      const newSessions = new Map(state.sessions);
      for (const [id] of newSessions) {
        if (id.startsWith(moduleNamespace)) {
          newSessions.delete(id);
        }
      }
      const activeCleared =
        state.activeSessionId && state.activeSessionId.startsWith(moduleNamespace)
          ? null
          : state.activeSessionId;
      return { sessions: newSessions, activeSessionId: activeCleared };
    });
  },

  getActiveSession: () => {
    const { sessions, activeSessionId } = get();
    return activeSessionId ? sessions.get(activeSessionId) : undefined;
  },

  getActiveScene: () => {
    const session = get().getActiveSession();
    if (!session) return undefined;
    return session.scenes[session.activeSceneIndex];
  },
}));

// =============================================================================
// BROADCAST CHANNEL SYNC — syncs session plan state between windows
// =============================================================================

const GM_CHANNEL = 'machina-gm';
const _windowId = crypto.randomUUID();
let _channel: BroadcastChannel | null = null;
let _suppressBroadcast = false;
let _unsubscribe: (() => void) | null = null;

interface SessionPlanSyncPayload {
  type: 'SESSION_PLAN_STATE' | 'SESSION_PLAN_REQUEST_SYNC';
  _windowId: string;
  sessions?: [string, SessionPlan][];
  activeSessionId?: string | null;
}

function broadcastSessionPlanState(): void {
  if (!_channel || _suppressBroadcast) return;
  const { sessions, activeSessionId } = useSessionPlanStore.getState();
  const payload: SessionPlanSyncPayload = {
    type: 'SESSION_PLAN_STATE',
    _windowId,
    sessions: Array.from(sessions.entries()),
    activeSessionId,
  };
  _channel.postMessage(payload);
}

function applyRemoteState(payload: SessionPlanSyncPayload): void {
  if (!payload.sessions) return;
  _suppressBroadcast = true;
  try {
    const sessions = new Map(payload.sessions);
    useSessionPlanStore.setState({
      sessions,
      activeSessionId: payload.activeSessionId ?? null,
    });
  } finally {
    _suppressBroadcast = false;
  }
}

export function requestSessionPlanSync(): void {
  if (!_channel) return;
  _channel.postMessage({
    type: 'SESSION_PLAN_REQUEST_SYNC',
    _windowId,
  } satisfies SessionPlanSyncPayload);
}

export function initSessionPlanSync(): void {
  // Already initialized
  if (_channel) return;

  _channel = new BroadcastChannel(GM_CHANNEL);

  _channel.onmessage = (event: MessageEvent<SessionPlanSyncPayload>) => {
    const data = event.data;
    // Ignore our own broadcasts
    if (data._windowId === _windowId) return;

    if (data.type === 'SESSION_PLAN_STATE') {
      applyRemoteState(data);
    } else if (data.type === 'SESSION_PLAN_REQUEST_SYNC') {
      // Another window is asking for current state — send ours
      broadcastSessionPlanState();
    }
  };

  // Subscribe to store changes and broadcast outbound
  _unsubscribe = useSessionPlanStore.subscribe(() => {
    broadcastSessionPlanState();
  });
}

export function destroySessionPlanSync(): void {
  _unsubscribe?.();
  _unsubscribe = null;
  _channel?.close();
  _channel = null;
}

// =============================================================================
// SKOLNIT SOLO SESSION: "Who Poisoned the Winner?"
// =============================================================================

export function loadSkolnitSession(): SessionPlan {
  const session: SessionPlan = {
    id: 'session-skolnit-poison',
    name: 'Who Poisoned the Winner?',
    subtitle: 'Port Nyanzaru after dark \u2014 glitter above, rot underneath',
    tone: 'Night-set noir investigation with a chase climax. The whole session runs after sundown \u2014 Skolnit is the predator in the city. Social, then stealth-hunt, then action.',
    lengthMinutes: [60, 90],
    coreLoop: 'social \u2192 clues \u2192 stealth-hunt \u2192 chase \u2192 reveal \u2192 Lady E hook',
    truth: 'A fixer paid a handler to poison a rival stable\u2019s "lucky tonic" to trigger a public collapse and spark panic about "zombie sickness," tanking odds and enabling a huge payout. The toxin is a blended necro-reagent (not a true undead disease) that mimics the outbreak symptoms.',

    activeSceneIndex: 0,
    status: 'prep',
    createdAt: new Date(),
    updatedAt: new Date(),

    // ----- NPCs -----
    npcs: [
      {
        id: 'npc-lady-e',
        name: 'Lady Eshera "E" Vorn',
        alias: 'Lady E',
        role: 'Masked noble patron tied to the Merchant Princes\u2019 quiet security (off-the-books)',
        description: 'She\u2019s running a quiet war against whoever is manufacturing "fear outbreaks." She wants Skolnit because he can operate unseen, he can track, and he can kill quietly if it comes to that. Only moves at night.',
        style: 'Calm, surgical, never raises her voice. Offers help like it\u2019s a contract.',
        tell: 'An emerald signet with a stylized "E" (curling vine/wave motif). Faint scent of clove + rain.',
        pressureLine: '"You\u2019re good in the dark. I need someone who can follow a trail without leaving one."',
        alternates: [
          'Emerald Enclave agent (E = Enclave): wants to stop necromancy from poisoning the jungle. Recruits Skolnit as a scout.',
          'Red Wizard turncoat: knows the reagent recipe, wants to sabotage her former allies, needs cover.',
        ],
      },
      {
        id: 'npc-old-moke',
        name: 'Old Moke',
        role: 'Apothecary / back-alley herbalist',
        description: 'Operates behind a spice shop. Recognizes the reagent as a necromantic mimic\u2014it imitates undead symptoms to cause fear, but isn\u2019t a true disease. Scared of "the bettor."',
        style: 'Nervous, chatty when trusting, clams up when spooked.',
      },
      {
        id: 'npc-masked-bettor',
        name: 'The Masked Bettor',
        role: 'Fixer\u2019s agent / the one who placed the poisoned bets',
        description: 'Works for a fixer tied to necro-reagent shipments. Wears a cheap mask clasp stamped with a crude emerald motif (copying Lady E\u2019s mark). Will flee when spotted, leading to the rooftop chase.',
        style: 'Slippery, desperate when cornered, will try to deal.',
      },
    ],

    // ----- CLUE CARDS -----
    clueCards: [
      {
        id: 'clue-lady-e-coin',
        name: "Lady E's Coin",
        front: 'An old trade coin stamped with an emerald "E" (vine/wave motif).',
        back: 'The edge is nicked in a pattern\u2014like a deliberate mark. Proof Lady E was present. Can be used to gain entry to a later meeting.',
        discovered: false,
      },
      {
        id: 'clue-swapped-tonic',
        name: 'Swapped Tonic',
        front: 'A "lucky tonic" bottle that smells of citrus\u2026 and something metallic under it.',
        check: { ability: 'Nature/Medicine', dc: 12 },
        leadsTo: 'Apothecary source (Old Moke)',
        back: '"Compound, not disease." The metallic smell is a necro-reagent byproduct.',
        discovered: false,
      },
      {
        id: 'clue-odds-ledger',
        name: 'Odds Ledger Scrap',
        front: 'A torn page: "Panic spikes odds. Don\u2019t let them stabilize."',
        check: { ability: 'Investigation', dc: 13 },
        back: 'Identifies the handwriting style as a merchant clerk\u2019s.',
        discovered: false,
      },
      {
        id: 'clue-stable-key',
        name: 'Stable Key w/ Green Mud',
        front: 'Key to a tack room; mud is tinted green with crushed tile grit.',
        check: { ability: 'Survival', dc: 12 },
        leadsTo: 'Rooftop tiles near the market ridge (chase route)',
        back: 'Matches the distinctive green clay tiles on market-ridge rooftops.',
        discovered: false,
      },
      {
        id: 'clue-moke-diagnosis',
        name: "Moke's Diagnosis",
        front: '"This is a mimic reagent. It looks like undead sickness, but it\u2019s brewed."',
        leadsTo: 'Shuts down zombie-scare assumptions; changes how NPCs react.',
        discovered: false,
      },
      {
        id: 'clue-masked-token',
        name: 'Masked Token',
        front: 'A cheap mask clasp stamped with the same emerald motif as Lady E\u2019s coin\u2014but cruder.',
        back: 'Implication: someone is copying her mark. A false flag or a fan?',
        discovered: false,
      },
      {
        id: 'clue-bootprints',
        name: 'Bootprints in Rooftop Clay',
        front: 'Green-tinted tile grit mixed with stable mud. Someone went stable \u2192 rooftops \u2192 market ridge.',
        check: { ability: 'Survival', dc: 12 },
        leadsTo: 'Confirms the chase route; links stables to rooftop escape path.',
        back: 'A ranger can read the stride length \u2014 this person was running. Recently.',
        discovered: false,
      },
      {
        id: 'clue-reagent-scent',
        name: 'Reagent Scent Profile',
        front: 'Clove, bitter metal, citrus cover. You can track it like smoke on the wind.',
        check: { ability: 'Survival', dc: 13 },
        leadsTo: 'Lets Skolnit follow the reagent trail to Moke\u2019s shop \u2014 and later to the shipment.',
        back: 'The scent is distinctive enough to track across the city at night. A ranger advantage.',
        discovered: false,
      },
    ],

    // ----- SCENES -----
    scenes: [
      // SCENE 1: The Feast (Night Edition)
      {
        id: 'scene-feast',
        name: 'The Feast',
        location: 'VIP terrace above the coliseum \u2014 late night, torchlit',
        durationMinutes: [10, 15],
        description: 'The post-race celebration runs late into the night. Torches gutter on the terrace, casting long shadows. Toasts, wagers, handlers, nobles. Then a handler collapses mid-cheer. People shout "ZOMBIE SICKNESS!" \u2014 but something\u2019s wrong with the panic.',
        goal: 'Investigate the collapse. Spot Lady E. Gain the coin.',
        skillChecks: [
          {
            id: 'check-feast-medicine',
            ability: 'Medicine / Nature',
            dc: 12,
            description: 'Examine the collapsed handler',
            onSuccess: 'Not normal disease; bitter metallic aftertaste \u2192 "compound."',
            onFail: 'Looks like some kind of illness, hard to tell more.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-feast-perception-mimic',
            ability: 'Perception',
            dc: 13,
            description: 'Study the victim\u2019s symptoms up close',
            onSuccess: 'The victim\u2019s pupils don\u2019t "cloud" like true undead rot \u2014 this is a mimic, not the real thing.',
            onFail: 'Looks bad. Could be anything.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-feast-insight',
            ability: 'Insight',
            dc: 13,
            description: 'Read the crowd\u2019s reactions',
            onSuccess: 'One bettor is "performing fear" to whip the crowd. The panic is being amplified on purpose.',
            onFail: 'Everyone seems genuinely terrified.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-feast-perception',
            ability: 'Perception',
            dc: 14,
            description: 'Scan the terrace for anyone acting wrong',
            onSuccess: 'Spot a masked bettor already leaving before anyone calls for help.',
            onFail: 'The crowd is too chaotic to pick out individuals.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-feast-sleight',
            ability: 'Sleight of Hand',
            dc: 14,
            description: 'Lift a token/purse from a fleeing attendant',
            onSuccess: 'Gain a clue token with an emerald "E" stamp.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-feast-spot-e',
            ability: 'Perception',
            dc: 13,
            description: 'Notice Lady E at the edge of the terrace',
            onSuccess: 'She\u2019s watching the crowd, not the victim. She clocks Skolnit clocking her. A subtle nod \u2014 then she leaves a small coin on the railing.',
            onFail: 'You find the coin later, left on a railing. She was already gone.',
            resolved: false,
            passed: false,
          },
        ],
        clueCardIds: ['clue-lady-e-coin'],
        notes: 'NIGHT: Torchlit terrace, long shadows. Lady E\u2019s Coin is gained automatically whether or not Skolnit spots her. The Perception check determines if there\u2019s a personal moment \u2014 she sees him seeing her. This is her first assessment.\n\nKey detail: the mimic Perception check is important. If he catches it here, it changes the flavor of every subsequent scene from "zombie panic" to "someone manufactured this."',
        status: 'upcoming',
      },

      // SCENE 2: The Stables (Stealth-Combat Hybrid, Night)
      {
        id: 'scene-stables',
        name: 'The Stables',
        location: 'Racing stables \u2014 dead of night, lanterns guttering',
        durationMinutes: [15, 25],
        description: 'The racing stables after midnight. Long central aisle with stalls on both sides. A loft catwalk runs above. 2\u20133 pools of deep darkness between the few remaining lanterns. Hay-and-sweat smell. This is Gloom Stalker territory.',
        goal: 'Get inside unseen, find evidence, deal with guards. Reward ranger play.',
        backdrop: {
          environment: 'tavern_interior',
          mood: 'dark_ambient',
          prompt: 'racing stables at night, long central aisle, stalls on both sides, loft catwalk above, guttering lanterns, pools of deep shadow, hay and leather',
        },
        skillChecks: [
          {
            id: 'check-stables-pwt',
            ability: 'Stealth (Pass Without Trace)',
            dc: 18,
            description: 'Ghost into the stables completely unseen',
            onSuccess: 'Total infiltration \u2014 no one knows you were ever here. You can position freely before anything happens.',
            onFail: 'You get in, but a handler catches movement. The difference between "good stealth" and "ghost" matters here.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-stables-sneak',
            ability: 'Stealth',
            dc: 13,
            description: 'Sneak past the night watch (standard approach)',
            onSuccess: 'Slip past the night watch unnoticed.',
            onFail: 'A guard spots you\u2014talk fast or fight.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-stables-talk',
            ability: 'Deception / Persuasion',
            dc: 14,
            description: 'Talk your way past the handlers',
            onSuccess: 'They let you through ("We\u2019re all worried about what happened").',
            onFail: 'Handlers are too jumpy\u2014they call for the toughs.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-stables-lock',
            ability: "Thieves' Tools",
            dc: 13,
            description: 'Pick the tack room lock',
            onSuccess: 'Clean entry\u2014no one heard.',
            onFail: 'Lock pops but makes noise. You have 1 round before someone checks.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-stables-survival',
            ability: 'Survival',
            dc: 12,
            description: 'Read bootprints in the stable mud',
            onSuccess: 'Green tile grit mixed with stable mud \u2014 someone went from here to the rooftops recently.',
            onFail: 'Too many boot tracks to make out a clear trail.',
            resolved: false,
            passed: false,
          },
        ],
        encounter: {
          enemies: '1 Spy + 1 Thug (recommended) \u2014 or 2 Thugs (clean option)',
          notes: 'Layout: long aisle, loft catwalk, 2\u20133 pools of darkness between lanterns.\n\nDREAD AMBUSHER MOMENT: A lookout (Spy or Thug) turns the corner first. If Skolnit is hidden, he gets the drop \u2192 Dread Ambusher triggers: extra attack + 1d8 bonus damage + 10ft extra movement. This should feel amazing in solo.\n\nLANTERN GAPS: First time Skolnit attacks from darkness, enemies take \u20132 on their next Perception/attack roll as they panic-search the shadows. Horror-movie ranger moment.\n\nThe Spy tries to disengage and use darkness \u2014 feels like prey that bites back. The Thug is muscle.',
          nonlethalOption: 'If Skolnit drops one to 0 HP, they spill info about who hired them ("masked, paid in trade bars").',
          enemyList: [
            { name: 'Spy', count: 1, ac: 12, maxHp: 27, str: 10, dex: 15, con: 10, int: 12, wis: 14, cha: 16, size: 'medium' },
            { name: 'Thug', count: 1, ac: 11, maxHp: 32, str: 15, dex: 11, con: 14, int: 10, wis: 10, cha: 11, size: 'medium' },
          ],
        },
        clueCardIds: ['clue-swapped-tonic', 'clue-odds-ledger', 'clue-stable-key', 'clue-bootprints'],
        notes: 'NIGHT / GLOOM STALKER PLAYGROUND: Long aisle with darkness pools = perfect for Skolnit. The loft catwalk gives height advantage. Let him set up an ambush if he wants.\n\nPASS WITHOUT TRACE: DC 18 stealth = the "ghost" tier. With PWT (+10), a decent Stealth roll makes this trivial. Without it, DC 13 is the normal approach. Make the difference felt: ghost-tier means he can position himself in the loft before combat, read all the clues uninterrupted, or skip the encounter entirely.\n\nFour findings: swapped tonic, ledger page, stable key with green mud, bootprints.',
        status: 'upcoming',
      },

      // SCENE 3: Back-alley Meet (Tracker Win)
      {
        id: 'scene-alley',
        name: 'Back-alley Meet',
        location: 'Behind a spice shop / apothecary stall \u2014 lamplit alley, night',
        durationMinutes: [10, 15],
        description: 'Instead of following directions, Skolnit can track the reagent scent directly: clove + bitter metal + citrus cover. The trail leads through the night market to a shuttered spice shop. Old Moke is the source.',
        goal: 'Track the reagent. Learn the truth. Trigger the chase.',
        backdrop: {
          environment: 'street_slum',
          mood: 'torchlit',
          prompt: 'narrow alley behind a spice shop at night, single oil lamp, shuttered windows, herb bundles hanging, cobblestones wet',
        },
        skillChecks: [
          {
            id: 'check-alley-track',
            ability: 'Survival',
            dc: 13,
            description: 'Follow the reagent scent trail (clove + bitter metal) to the right stall',
            onSuccess: 'You arrive early, overhear names through the shutters. Advantage on social checks in this scene.',
            onFail: 'You find the shop, but Moke hears you coming and is on guard.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-alley-persuade',
            ability: 'Persuasion',
            dc: 13,
            description: 'Get Moke to share who bought the reagent',
            onSuccess: '"Masked, paid in stamped trade bars. Didn\u2019t want to know names."',
            onFail: 'Moke clams up\u2014but drops hints if pressed.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-alley-intimidate',
            ability: 'Intimidation',
            dc: 14,
            description: 'Scare Moke into talking',
            onSuccess: 'Moke panics and blurts too much\u2014but this adds Heat.',
            onFail: 'Moke shuts down completely. You\u2019ll need another angle.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-alley-insight',
            ability: 'Insight',
            dc: 13,
            description: 'Read Moke\u2019s body language',
            onSuccess: 'He\u2019s scared of someone specific: "the bettor." Eyes dart to the alley mouth.',
            onFail: 'He seems nervous but you can\u2019t pin down why.',
            resolved: false,
            passed: false,
          },
        ],
        clueCardIds: ['clue-moke-diagnosis', 'clue-reagent-scent'],
        notes: 'TRACKER WIN: If Skolnit nails the Survival check, he arrives early and overhears conversation through the shutters \u2014 grants advantage on Persuasion/Intimidation. This is the ranger "I found you" moment.\n\nTrigger: the masked bettor spots Skolnit from the alley mouth and bolts \u2192 chase begins.\n\n\u2014\u2014\u2014 OVERHEARD DIALOGUE (read aloud if Survival passed) \u2014\u2014\u2014\n\nOld Moke (hushed, shaking): \u201c\u2014I told you, it\u2019s not the sickness. It only plays at it. One sip, the sweat, the tremor, the\u2026 grey look. Long enough for the crowd to scream.\u201d\n\nMasked Bettor (low, impatient): \u201cGood. Panic moves coin. The board already swung.\u201d\n\nRunner (younger): \u201cHe dropped at the toast. Exactly like you said.\u201d\n\nOld Moke: \u201cYou said nonlethal. If you doubled the measure\u2014\u201d\n\nMasked Bettor: \u201cI didn\u2019t double anything. I want him breathing. I want the rumor breathing.\u201d\n\n(A small clink\u2014metal on wood.)\n\nOld Moke: \u201cThat stamp again\u2026 Those aren\u2019t city bars.\u201d\n\nMasked Bettor: \u201cAnd you don\u2019t want to know where they\u2019re minted. Next batch goes out at dawn\u2014same route. No more cooking it inside the city.\u201d\n\nRunner: \u201cDawn from the river docks?\u201d\n\nMasked Bettor: \u201cFrom the lantern with the three nicks. You hand it off, you walk away.\u201d\n\nOld Moke (swallows): \u201cShe was there tonight.\u201d\n\nMasked Bettor (sharp): \u201cDon\u2019t say her name.\u201d\n\nOld Moke: \u201cThe emerald lady. I felt eyes. If she\u2019s sniffing\u2014\u201d\n\nMasked Bettor: \u201cThen we make sure she sniffs the wrong trail. Your mark\u2019s already on the bottle. By tomorrow the Princes will think she did it.\u201d\n\nRunner: \u201cAnd if the ranger\u2014 the quiet one\u2014\u201d\n\nMasked Bettor: \u201cIf he\u2019s smart, he\u2019ll chase the rooftops and feel clever. If he\u2019s not, he\u2019ll die in the alley and nobody will miss him.\u201d\n\n(Someone slides paper\u2014like a torn ledger page.)\n\nMasked Bettor: \u201cMemorize it. Burn it. Dawn. No mistakes.\u201d',
        status: 'upcoming',
      },

      // SCENE 4: Rooftop Chase (Gloom Stalker Edition)
      {
        id: 'scene-chase',
        name: 'Rooftop Chase',
        location: 'Port Nyanzaru rooftops, market ridge \u2014 moonlit, patches of deep shadow',
        durationMinutes: [10, 10],
        description: 'The masked bettor bolts across Port Nyanzaru\u2019s moonlit rooftops. Skolnit pursues through patches of deep shadow and torchlit gaps. The city sprawls below.',
        goal: 'Catch the bettor before they escape.',
        backdrop: {
          environment: 'street_medieval',
          mood: 'moonlit',
          prompt: 'Port Nyanzaru rooftops at night, weathered grey clay tiles, cracked plaster walls, palm fronds, patches of deep shadow between crumbling parapets, moonlit, grime-stained gutters',
        },
        skillChecks: [],
        chase: {
          name: 'Rooftop Pursuit',
          progressNeeded: 6,
          failuresAllowed: 3,
          currentProgress: 0,
          currentFailures: 0,
          baseDC: 13,
          options: [
            { ability: 'Acrobatics', description: 'Leap awnings / roof gaps', onFail: 'Take 1d4 damage + Failure' },
            { ability: 'Athletics', description: 'Shoulder through crowd / climb faster' },
            { ability: 'Stealth', description: 'Cut through shadows to shorten distance', progressOnSuccess: 2 },
            { ability: 'Perception', description: 'Spot a shortcut / anticipate turns' },
            { ability: 'Survival', description: 'Track scent / read bootprints to predict route' },
            { ability: 'Sleight of Hand', description: 'Scatter fruit, snag cloak (imposes disadvantage next round)' },
            { ability: 'Ranged Attack (Rope/Arrow)', description: 'Pin cloak/strap to awning or line (Attack DC 13)', progressOnSuccess: 2, onFail: 'Arrow goes wide \u2014 no progress' },
          ],
          complications: [
            { roll: 1, description: 'Rooftop pigeons explode into flight', check: { ability: 'Perception', dc: 13 }, onFail: 'Lose 1 Progress' },
            { roll: 2, description: 'A wet tile slips', check: { ability: 'Acrobatics', dc: 13 }, onFail: 'Failure + prone moment' },
            { roll: 3, description: 'The bettor ducks into a pitch-dark alley', check: { ability: 'Perception (Darkvision)', dc: 12 }, onFail: 'Lose 1 Progress \u2014 unless Gloom Stalker darkvision' },
            { roll: 4, description: 'A watch patrol shouts "Stop!"', check: { ability: 'Deception', dc: 14 }, onFail: 'Add 1 Failure' },
            { roll: 5, description: 'A caged raptor squeals', check: { ability: 'Animal Handling', dc: 12 }, onFail: 'Chaos \u2014 lose 1 Progress' },
            { roll: 6, description: 'The bettor throws a pouch of coins', check: { ability: 'Wis Save', dc: 12 }, onFail: 'Reflex-grab \u2014 lose 1 Progress' },
          ],
          specialRules: [
            'Dread Ambusher: Skolnit can invoke his first-round burst speed (+10 ft) once to gain advantage on a chase check ("closing the gap fast").',
            'Hunter\u2019s Mark (Bonus Action): If Skolnit marks the bettor, future Perception and Survival chase checks gain advantage \u2014 supernatural focus on the quarry.',
            'Pass Without Trace: If active, Skolnit can slip past the watch patrol complication automatically (no Deception needed).',
            'Darkvision Moment: Complication 3 \u2014 the bettor tries to vanish into a pitch-dark alley. With Gloom Stalker darkvision (90 ft), Skolnit sees him plain as day. Without it, this is a lantern-flare-and-shadow "oh, Gloom Stalker" moment.',
          ],
        },
        clueCardIds: [],
        notes: 'Win: 6 Progress before 3 Failures. On win, bettor is cornered \u2192 Scene 5.\n\nRANGER-ONLY ACTIONS: Hunter\u2019s Mark for advantage on tracking checks. Rope/Arrow Trick for +2 Progress (cinematic ranger moment). Survival option to track scent/bootprints.\n\nDARKVISION MOMENT: Complication 3 is designed to make Gloom Stalker darkvision shine. The bettor thinks he\u2019s lost in the dark. He hasn\u2019t.',
        status: 'upcoming',
      },

      // SCENE 5: The Reveal (Gloom Stalker Hook)
      {
        id: 'scene-reveal',
        name: 'The Reveal',
        location: 'Dead-end rooftop / balcony overlooking the harbor \u2014 moonlit',
        durationMinutes: [10, 15],
        description: 'The cornered bettor, chest heaving, offers a deal. Then Lady E steps from the shadows \u2014 she was already here, waiting.',
        goal: 'Get the truth. Meet Lady E properly. Accept the hook.',
        backdrop: {
          environment: 'docks',
          mood: 'moonlit',
          prompt: 'dead-end rooftop balcony at night, crumbling stone railing, weathered wood, moonlit, cornered, dim lantern glow from streets far below, dark tropical sky',
        },
        skillChecks: [
          {
            id: 'check-reveal-insight',
            ability: 'Insight',
            dc: 12,
            description: 'Is the bettor telling the truth?',
            onSuccess: 'Mostly\u2014they\u2019re genuinely scared of the fixer. This is bigger than one race.',
            onFail: 'Hard to read. They seem desperate enough to say anything.',
            resolved: false,
            passed: false,
          },
        ],
        clueCardIds: ['clue-masked-token'],
        notes: 'Bettor: "This wasn\u2019t to kill him. It was to make the city afraid. Fear moves coin."\n\nLady E steps from the shadows. Her offer (short, usable at table):\n\n"You\u2019re good in the dark. I need someone who can follow a trail without leaving one. The poison wasn\u2019t to kill a handler \u2014 it was to move the city with panic. Help me find who\u2019s shipping the reagent\u2026 and I\u2019ll point you to what\u2019s causing the dead to rise."\n\nThe hook forward:\nThe next shipment goes out at dawn via river barge (jungle prep) \u2014 or is being laundered through a Merchant Prince\u2019s salon (city intrigue). Either way, it leads deeper.\n\n"Tomorrow, the next bottle leaves the city. If it reaches the jungle, you\u2019ll be fighting \'zombies\' again \u2014 only next time, they\u2019ll be pointed at you."',
        status: 'upcoming',
      },
    ],
  };

  // Register in the store
  useSessionPlanStore.getState().addSession(session);
  return session;
}

// =============================================================================
// SESSION 2: "Silk and Venom" — The Party Meets Lady E
// =============================================================================

export function loadLadyESession(): SessionPlan {
  const session: SessionPlan = {
    id: 'session-lady-e-silk',
    name: 'Silk and Venom',
    subtitle: 'Every thread she pulls was already tied to your wrist',
    tone: 'Social intrigue with buried mystery. Lady E is generous, charming, and three moves ahead. The party should leave feeling helped — and slightly uneasy. Omu is never mentioned by name.',
    lengthMinutes: [90, 120],
    coreLoop: 'summons → salon (social reads) → the pitch → intel package (buried breadcrumbs) → jungle prep → departure',
    truth: 'Lady E is a chaos agent: her MO is "shake the web, see what falls out." She doesn\'t know exactly where the reagent comes from, but she suspects something ancient in the jungle. She\'s sending the party to cause disruption along the supply chain — any intel they shake loose benefits her. She wins in every scenario: if they find the source, she controls it; if they find the manufacturer, she eliminates a competitor; if they just cause chaos, her rivals get exposed. She has buried Omu breadcrumbs (maps, route markers, old ruins) throughout the intel package without ever naming the place. The party will stumble toward Omu thinking it was their own discovery.',

    activeSceneIndex: 0,
    status: 'prep',
    createdAt: new Date(),
    updatedAt: new Date(),

    // ----- NPCs -----
    npcs: [
      {
        id: 'npc-lady-e-2',
        name: 'Lady Eshera "E" Vorn',
        alias: 'Lady E',
        role: 'Fixer-patron disguised as a philanthropist. Jungle-access monopoly builder.',
        description: 'She ran Skolnit through a solo test ("Who Poisoned the Winner?") and he passed. Now she\'s bringing in his whole crew. She\'s already researched every party member — she knows about Dax\'s flying machine obsession, Bobby\'s ghost projection, Juju\'s independence, and Skolnit\'s ranger instincts. She never asks directly. She creates situations where people volunteer.',
        style: 'Warm but precise. Laughs easily but never at anything accidental. Pours your drink before you realize you\'re thirsty. Every kindness has geometry.',
        tell: 'Emerald signet (vine/wave motif). Clove + rain scent. She touches her ring when she\'s about to lie.',
        pressureLine: '"I don\'t need people I can control. I need people who break things I can\'t reach."',
        alternates: [
          'Emerald Enclave agent building a jungle intelligence network. Genuinely wants to stop necromancy but will sacrifice pawns.',
          'Former Merchant Prince advisor who was ousted. Rebuilding power through proxies and favors.',
        ],
      },
      {
        id: 'npc-shago',
        name: 'Shago',
        role: 'Lady E\'s majordomo / logistics fixer',
        description: 'A lean Chultan man with grey-streaked locs and a quiet voice. Ex-jungle guide who now runs Lady E\'s operations. He\'s the one who assembled the intel package. Genuinely competent and slightly protective of Lady E — not a lackey, a partner. He handles the "how" while she handles the "why."',
        style: 'Economical with words. Says "mm" instead of yes. Draws maps from memory. Sizes people up by watching their hands.',
        tell: 'A carved bone whistle around his neck — jungle signal tool. He plays with it when uncomfortable.',
        pressureLine: '"The jungle doesn\'t care what you meant to do. Only what you did."',
      },
      {
        id: 'npc-flask',
        name: 'Flask',
        role: 'Jungle trader / Lady E\'s field contact (and unknowing informant)',
        description: 'A chatty halfling merchant who trades up and down the River Soshenstar. Thinks he works for a "trade consortium." Actually feeds Lady E intel on jungle movements. Knows the river routes, the portage points, and which trails are safe. He\'ll be the party\'s guide into the jungle — and he genuinely doesn\'t know Lady E\'s full agenda.',
        style: 'Talks too much, laughs too loud, remembers every price he ever quoted. Disarming. Actually quite sharp underneath.',
        tell: 'Wears mismatched boots — one Chultan leather, one dwarven make. Claims they\'re lucky.',
      },
      {
        id: 'npc-prince-kwayothe',
        name: 'Merchant Prince Kwayothé',
        role: 'Lady E\'s rival / provides a warning about her',
        description: 'One of Port Nyanzaru\'s Merchant Princes. She controls the sale of tej, wine, ale, and poison. She and Lady E have a cold mutual respect — they understand each other\'s games. If the party visits Kwayothé\'s district during prep, she\'ll drop hints about Lady E\'s true nature: "A generous woman. Generous women frighten me."',
        style: 'Direct, amused, treats conversations like transactions. Always eating or drinking something.',
      },
    ],

    // ----- CLUE CARDS -----
    clueCards: [
      {
        id: 'clue-omu-map-fragment',
        name: 'Annotated Jungle Map',
        front: 'A detailed map of the River Soshenstar basin. Several trails are marked with supply-route annotations. One area deep in the jungle is circled in old ink with the note: "Avoid — old markers, no safe passage." The circle doesn\'t match any known settlement.',
        back: 'The circled area corresponds to the ruins of Omu. Lady E marked it as "avoid" to make the party curious. The "old markers" are Omuan boundary stones. An Investigation or History check reveals the circle matches pre-Spellplague cartography of a lost city.',
        check: { ability: 'History / Investigation', dc: 14 },
        leadsTo: 'Omu — without ever naming it',
        discovered: false,
      },
      {
        id: 'clue-trade-bar-stamps',
        name: 'Foreign Trade Bars',
        front: 'A set of trade bars stamped with an unfamiliar mint mark — not Nyanzaru, not Baldur\'s Gate, not Amn. The stamp shows a stepped pyramid with nine points around it.',
        back: 'The nine-pointed stamp matches descriptions of the Trickster Gods\' shrines in Omuan ruins. These trade bars are being used as currency along the reagent supply chain — someone is minting them at or near the source.',
        check: { ability: 'History', dc: 15 },
        leadsTo: 'Connects the reagent supply chain to an ancient site with pyramids and shrines',
        discovered: false,
      },
      {
        id: 'clue-reagent-recipe',
        name: 'Reagent Composition Notes',
        front: 'Shago\'s analysis of the necro-reagent from the poisoning: "Compound, not natural. Three base ingredients — two are jungle botanicals (identified). Third is unknown. Mineral or fungal. Not found in any coastal supply chain. Source is inland, deep."',
        back: 'The third ingredient is a fungal compound that only grows in specific underground conditions — warm, humid, near old magical infrastructure. The kind of environment found in buried temple complexes.',
        check: { ability: 'Nature / Arcana', dc: 13 },
        leadsTo: 'The reagent source requires ancient underground infrastructure — not just jungle',
        discovered: false,
      },
      {
        id: 'clue-dead-drops',
        name: 'Supply Chain Dead Drops',
        front: 'A list of six dead-drop locations along the River Soshenstar, spaced roughly a day\'s travel apart. The last one is marked "terminal — mule returns empty." After that, the trail goes cold.',
        back: 'The dead drops trace a path that leads directly toward the Omu region. The "terminal" point is about two days\' travel from the ruins. Whoever is manufacturing the reagent doesn\'t want anyone following the supply chain to its end.',
        discovered: false,
      },
      {
        id: 'clue-lady-e-ledger',
        name: 'Lady E\'s Private Ledger Page',
        front: 'A page from Lady E\'s records, left visible "by accident." It shows payments to three different jungle expeditions over the past year — all failed, all headed roughly the same direction. Notes in the margin: "Lost contact day 12." "Turned back — \'wrong air.\'" "No survivors."',
        back: 'Lady E has already sent three teams toward Omu. All failed. The party is attempt four. She hasn\'t told them this. The "wrong air" note suggests something supernatural at the destination.',
        check: { ability: 'Insight / Investigation', dc: 14 },
        leadsTo: 'Lady E has been trying to reach this place for a year. The party isn\'t her first attempt.',
        discovered: false,
      },
      {
        id: 'clue-lady-e-true-nature',
        name: 'Kwayothé\'s Warning',
        front: '"A generous woman. Generous women frighten me." Kwayothé leans back and studies you. "She doesn\'t collect debts. She collects dependencies. By the time you realize you owe her, you\'ll have been paying for months."',
        back: 'Kwayothé knows Lady E\'s pattern: she creates situations where people need her help, then she "generously" provides it. The party\'s mission is designed so they\'ll need her contacts, her maps, her supplies — and they\'ll return owing her more than they left with.',
        discovered: false,
      },
      {
        id: 'clue-dax-schematic',
        name: 'Airship Workshop Rumor',
        front: 'Shago mentions offhandedly that one of the failed expeditions reported seeing "a structure with canvas rigging — like a dock, but above the canopy." He marks it on the map. "Probably nothing. Old Chultan engineering, if it ever existed."',
        back: 'Bait for Dax. Lady E knows about his flying machine obsession. Whether the "aerial structure" exists or not, it\'s designed to make Dax personally invested in reaching the destination. Shago delivered the line on cue.',
        discovered: false,
      },
      {
        id: 'clue-ghost-veil',
        name: 'The Thin Veil',
        front: 'Flask mentions that upriver, past the third portage, the jungle "gets weird." Animals go quiet. His compass spins. "And at night, you see people who aren\'t there. Walking between the trees. Not threatening — just... present. Like echoes."',
        back: 'Bait for Bobby. The "thin veil" phenomenon near Omu would amplify ghost projection abilities. Lady E wants Bobby interested in going deeper. The "echoes" may be genuine — residual spirits from Omu\'s fall.',
        discovered: false,
      },
    ],

    // ----- SCENES -----
    scenes: [
      // SCENE 1: The Silk House
      {
        id: 'scene-silk-house',
        name: 'The Silk House',
        location: 'A private villa above the harbor — torchlit courtyard, silk canopies, jasmine',
        durationMinutes: [15, 25],
        description: 'Each party member received a different summons. Skolnit: a note tucked into the emerald coin from the solo session — "Bring your friends." Dax: a crate delivered to his workshop containing a rare gear assembly and a card reading "I hear you\'re building something impossible. Come discuss feasibility." Juju: her usual supplier of [whatever she needs] has dried up — the only lead points here. Bobby: a sealed letter with no return address that simply reads "The dead remember you. So do I."\n\nThey arrive at a harbour-front villa draped in silk canopies. Lanterns hang from jasmine vines. It smells expensive. Shago meets them at the gate, sizes them up without speaking, and leads them through a courtyard to an open-air terrace where Lady E waits with wine already poured.',
        goal: 'Establish Lady E\'s domain. Let the party feel welcomed and subtly assessed. Each character should feel she already knows something about them.',
        backdrop: {
          environment: 'tavern_interior',
          mood: 'cozy',
          prompt: 'private villa courtyard at night, silk canopies overhead, jasmine vines on stone columns, torchlit, wine on the table, warm evening, enclosed garden walls, lanterns, elegant but weathered',
        },
        skillChecks: [
          {
            id: 'check-silk-insight-e',
            ability: 'Insight',
            dc: 16,
            description: 'Read Lady E\'s true intentions',
            onSuccess: 'She\'s genuinely pleased to see you — but the way a chess player is pleased when the pieces arrive. She\'s been expecting you. She arranged for you to come.',
            onFail: 'She seems warm, open, genuinely interested. A generous host.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-silk-insight-shago',
            ability: 'Insight',
            dc: 13,
            description: 'Read Shago\'s body language',
            onSuccess: 'He\'s not a servant — he\'s evaluating you. His eyes track your hands, your weapons, your positioning. Ex-military or ex-guide. He respects competence.',
            onFail: 'Quiet, professional. Seems like staff.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-silk-perception-villa',
            ability: 'Perception',
            dc: 13,
            description: 'Study the villa for clues about Lady E',
            onSuccess: 'No servants besides Shago. Multiple exits — she chose a space she can leave quickly. The books on the shelf are maps and expedition journals, not decoration. This is a working office disguised as a parlor.',
            onFail: 'Beautiful space. She clearly has wealth and taste.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-silk-investigation-summons',
            ability: 'Investigation',
            dc: 12,
            description: 'Compare notes on how each party member was summoned',
            onSuccess: 'Every invitation was tailored. She researched each of you individually. This wasn\'t a mass mailing — it was four separate operations.',
            onFail: 'You all got invitations. Seems like she\'s recruiting a group.',
            resolved: false,
            passed: false,
          },
        ],
        clueCardIds: [],
        notes: 'TONE: Warm, elegant, slightly too perfect. Lady E pours wine before anyone asks. She remembers names. She asks questions that show she already knows the answers.\n\nSKOLNIT: She acknowledges the solo job with a nod and a half-smile — "You were quiet. I appreciate quiet." Nothing more. She doesn\'t praise excessively.\n\nDAX: She asks about "the machine" casually, like she\'s making conversation. "I hear you\'re trying to solve a problem no one else is working on. I like that."\n\nJUJU: She doesn\'t try to charm Juju. Instead, she\'s bluntly honest: "I redirected your supply line. I apologize. I needed you to come, and you don\'t respond to invitations." This is the correct read — Juju respects directness.\n\nBOBBY: She mentions the letter and watches his reaction. "The dead leave echoes. You hear them, don\'t you? I\'ve encountered something similar — deeper in the jungle, they\'re louder."\n\nSHAGO: He serves the drinks but positions himself where he can watch the exits. He doesn\'t sit.',
        status: 'upcoming',
      },

      // SCENE 2: The Pitch
      {
        id: 'scene-pitch',
        name: 'The Pitch',
        location: 'The same terrace — after the wine, maps spread on the table',
        durationMinutes: [20, 30],
        description: 'Lady E shifts from host to briefer. She doesn\'t say "I want to hire you." Instead, she describes a problem — the necro-reagent supply chain — and lets the party realize they\'re already involved (Skolnit\'s solo session). Then she lays out what she knows, what she doesn\'t know, and what she needs.\n\n"Someone is manufacturing fear. The reagent from the race — Skolnit, you saw what it does — it\'s not a one-off. It\'s a supply chain. Bottles move upriver, finished product comes back down. I\'ve traced six dead drops along the Soshenstar. After the sixth..." She taps the map. "The trail dies."\n\nShe doesn\'t say what\'s at the end. She lets them look at the map and draw their own conclusions.',
        goal: 'Deliver the task. Plant individual hooks. Bury Omu breadcrumbs in the operational details. The party should feel like they\'re choosing to go — not being sent.',
        backdrop: {
          environment: 'tavern_interior',
          mood: 'torchlit',
          prompt: 'villa courtyard at night, maps spread on a wooden table, expedition planning, warm torchlight, stone walls draped in cloth, candles, no windows to outside',
        },
        skillChecks: [
          {
            id: 'check-pitch-insight-lie',
            ability: 'Insight',
            dc: 17,
            description: 'Is Lady E withholding something?',
            onSuccess: 'She\'s telling the truth about the reagent — but she knows more about the destination than she\'s sharing. She\'s been here before. Not physically. But she\'s sent people.',
            onFail: 'She seems transparent. Frustrated, even — like someone who\'s been working a problem alone for too long and finally has help.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-pitch-insight-hooks',
            ability: 'Insight',
            dc: 15,
            description: 'Notice how she tailors her pitch to each person',
            onSuccess: 'She\'s running four conversations simultaneously. Each "casual" mention — the airship, the echoes, the polite society introduction, the supply disruption — is precision-targeted. She studied you.',
            onFail: 'She\'s addressing the group but making each person feel included. Good leadership.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-pitch-history-map',
            ability: 'History',
            dc: 14,
            description: 'Study the circled area on the map',
            onSuccess: 'The "avoid" zone matches old Amnian trade records of a lost Chultan city — pre-Spellplague. The name was scrubbed from most modern maps after the city was "consumed by the jungle." Nobody goes there.',
            onFail: 'Unfamiliar area. Deep jungle, no known settlements.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-pitch-arcana-reagent',
            ability: 'Arcana / Nature',
            dc: 13,
            description: 'Analyze the reagent composition notes',
            onSuccess: 'The unknown third ingredient needs specific conditions: warm, humid, underground, near residual magical infrastructure. Natural caves won\'t do — this grows in ruins.',
            onFail: 'Complex compound. The third ingredient is a mystery.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-pitch-persuasion-pay',
            ability: 'Persuasion',
            dc: 12,
            description: 'Negotiate payment / terms',
            onSuccess: 'Lady E smiles — she respects the negotiation. She offers upfront trade bars (200 gp equivalent) plus "first claim on anything you find that isn\'t the reagent." Shago adds: "And a guide. Flask knows the river."',
            onFail: 'She offers the standard package without the bonus. Still generous.',
            resolved: false,
            passed: false,
          },
        ],
        clueCardIds: ['clue-omu-map-fragment', 'clue-trade-bar-stamps', 'clue-reagent-recipe', 'clue-dead-drops'],
        notes: 'INDIVIDUAL HOOKS (weave these into the conversation naturally):\n\nSKOLNIT — "The Merchant Princes are starting to notice you. After last night, you have a reputation. I could introduce you to people who matter — people who could make your life in this city very different." She lets it hang. She\'s offering polite society access.\n\nDAX — Shago unfolds a side map. "One of our scouts — before we lost contact — reported a structure above the canopy. Canvas rigging, wooden frame. Like a dock in the sky." He glances at Lady E. She shrugs. "Probably old Chultan engineering. Or nothing. But if you happen to pass through..." Pure bait.\n\nJUJU — Lady E doesn\'t hook Juju. Instead, she\'s honest: "I can\'t offer you anything you\'d want. You don\'t need my money, my connections, or my approval." Beat. "But something out there is manufacturing a weapon, and right now it\'s pointed at this city. That seems like the kind of problem you\'d solve on your own terms." She\'s appealing to Juju\'s independence — not trying to control her.\n\nBOBBY — "The scouts who went deepest reported something. At night, figures between the trees — not hostile, not alive. Echoes, they called them. Your... ability. It might work differently there. Stronger." She watches his reaction carefully. "I don\'t know what that means for you. But I thought you should know before you decide."\n\n---\n\nTHE OMU BREADCRUMBS: Never say "Omu." The clues are:\n1. Map with circled "avoid" zone (History DC 14 to connect to lost city)\n2. Trade bars with stepped-pyramid-and-nine-points stamp (History DC 15)\n3. Reagent needs underground magical infrastructure (Arcana/Nature DC 13)\n4. Dead drops trace toward the same area\n\nIf players connect the dots: Lady E smiles. "Interesting. I hadn\'t considered that." (She absolutely had.)',
        status: 'upcoming',
      },

      // SCENE 3: The Package
      {
        id: 'scene-package',
        name: 'The Package',
        location: 'The villa\'s inner study — maps pinned to walls, expedition crates',
        durationMinutes: [10, 15],
        description: 'Shago leads the party to an inner room. Crates of supplies are already packed. Maps are pinned to the walls with annotations in multiple handwriting styles — previous expeditions\' notes. Shago walks them through the route, the dead drops, the contacts. He\'s efficient and practical.\n\nThis is also where Lady E\'s ledger page is "accidentally" visible — showing her three previous failed expeditions.',
        goal: 'Deliver practical intel. Let players discover the ledger (previous failures). Plant the airship and ghost-veil clues.',
        backdrop: {
          environment: 'arcane_library',
          mood: 'torchlit',
          prompt: 'inner study of a villa, maps pinned to walls, expedition crates stacked, candlelight, leather-bound journals',
        },
        skillChecks: [
          {
            id: 'check-package-investigation-ledger',
            ability: 'Investigation',
            dc: 12,
            description: 'Notice Lady E\'s ledger page on the desk',
            onSuccess: 'Three previous expeditions, all failed. "Lost contact day 12." "Turned back — wrong air." "No survivors." She\'s sent teams before. You\'re attempt four.',
            onFail: 'You notice the desk has papers on it, but Shago draws your attention to the maps.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-package-insight-ledger',
            ability: 'Insight',
            dc: 14,
            description: 'Was the ledger left visible on purpose?',
            onSuccess: 'Yes. Lady E wanted you to see it. She\'s testing whether you\'ll confront her about the failed expeditions — or whether you\'ll go anyway. Either answer tells her something about you.',
            onFail: 'Seems like an oversight. She probably didn\'t mean for you to see it.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-package-survival-route',
            ability: 'Survival',
            dc: 12,
            description: 'Evaluate the proposed jungle route',
            onSuccess: 'The route avoids the most dangerous predator territories but passes through several areas known for undead activity. Shago knows the jungle — this is a real route, not a trap.',
            onFail: 'Looks like a standard river route. Hard to evaluate without being there.',
            resolved: false,
            passed: false,
          },
        ],
        clueCardIds: ['clue-lady-e-ledger', 'clue-dax-schematic', 'clue-ghost-veil'],
        notes: 'SHAGO\'S BRIEFING (practical, no nonsense):\n"River Soshenstar, three days upstream by canoe. Flask will guide — he knows the portage points. Six dead drops along the route. At each one, you\'ll find a cache marker. Follow them. After the sixth, the trail goes cold. That\'s where your job starts."\n\nHe hands them:\n- Waterproof map case with route annotations\n- Six cache marker descriptions (what to look for)\n- A signal whistle ("If you need extraction, blow twice at dawn. I\'ll be listening.")\n- Trade bars for supplies (upfront payment)\n\nTHE LEDGER: Lady E leaves the room briefly ("I need to speak with Flask"). The ledger page is face-up on the desk. This is a test. If someone confronts her:\n\nLady E (no hesitation): "Three teams. The first turned back. The second went silent. The third..." She pauses. "I sent eleven people in over fourteen months. Four came back. None of them reached the end." Beat. "You deserve to know that. You also deserve to know I think you\'re better than they were."\n\nShe\'s being honest. She\'s also being manipulative. Both things are true.\n\nDAX SCHEMATIC: Shago mentions the "aerial structure" while reviewing the route. It\'s marked on the map with a small sketch. He sells it as incidental — "I wouldn\'t detour for it, but if your path takes you near..." Lady E catches his eye. They\'ve rehearsed this.\n\nGHOST VEIL: Flask will mention this in the next scene, but Shago can preview it: "Flask says the jungle gets strange past the third portage. Compass issues. Sounds that shouldn\'t be there." He doesn\'t elaborate. Bobby should feel the pull.',
        status: 'upcoming',
      },

      // SCENE 4: Port Prep & The Underbelly
      {
        id: 'scene-port-prep',
        name: 'Port Prep',
        location: 'Port Nyanzaru — market, docks, Merchant Prince districts',
        durationMinutes: [20, 30],
        description: 'The party has the task, the intel, and the supplies. Now they prep. Shopping for jungle gear, meeting Flask at the docks, asking around about Lady E. This is the "free roam" scene — let the players decide what they want to do before departing.\n\nKey encounter: Merchant Prince Kwayothé (if players seek her out or visit her district). She knows Lady E and offers a warning that doubles as a clue about Lady E\'s true nature.',
        goal: 'Free prep. Meet Flask. Optional: Kwayothé warning. Players should leave this scene with everything they need — and one more reason to be careful about their patron.',
        backdrop: {
          environment: 'docks',
          mood: 'golden_hour',
          prompt: 'Port Nyanzaru docks at grey dawn, worn wooden piers, weathered cargo crates, faded awnings, muddy water, tropical humidity haze, departure morning, gritty and lived-in',
        },
        skillChecks: [
          {
            id: 'check-prep-streetwise',
            ability: 'Investigation (streetwise)',
            dc: 13,
            description: 'Ask around about Lady E in the market',
            onSuccess: '"Lady Vorn? Charitable woman. Sponsors healers, feeds orphans. Very generous." Everyone says the same thing — suspiciously the same thing. Like a rehearsed answer.',
            onFail: '"She\'s a good woman. Why do you ask?"',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-prep-kwayothe',
            ability: 'Persuasion / Intimidation',
            dc: 14,
            description: 'Get Kwayothé to speak plainly about Lady E',
            onSuccess: 'Kwayothé leans in: "She doesn\'t collect debts. She collects dependencies. By the time you realize you owe her, you\'ll have been paying for months. She once bankrupted a shipping magnate — and he thanked her for saving his business." She sips her drink. "Go on your expedition. Just count what you owe when you come back."',
            onFail: '"A generous woman. Generous women frighten me." She won\'t say more.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-prep-flask-read',
            ability: 'Insight',
            dc: 12,
            description: 'Read Flask\'s loyalties',
            onSuccess: 'Flask genuinely doesn\'t know Lady E\'s full agenda. He thinks he works for a "trade consortium." He\'s honest, competent, and cheerful — an authentic guide, not a plant. He\'s also unwittingly feeding Lady E intel through his regular reports.',
            onFail: 'He seems like a good guide. Experienced, friendly, knows the river.',
            resolved: false,
            passed: false,
          },
          {
            id: 'check-prep-supplies',
            ability: 'Survival',
            dc: 11,
            description: 'Optimize jungle supplies',
            onSuccess: 'Good loadout: insect repellent, rain cloaks, water purification herbs, hammock nets. Flask nods approvingly. "You\'ve done this before."',
            onFail: 'Standard kit. Flask quietly adds a few items to the pile.',
            resolved: false,
            passed: false,
          },
        ],
        clueCardIds: ['clue-lady-e-true-nature'],
        notes: 'FREE ROAM: Let the players drive. Suggested beats:\n\n1. FLASK AT THE DOCKS: He\'s loading the canoes. Chatty, friendly, talks about the river. Mentions the "thin veil" phenomenon unprompted if Bobby is present ("You hear things upriver. Past the third portage. Voices. Not threatening — just... present."). If players ask about the destination, he\'s genuinely confused: "End of the supply chain? I didn\'t know there was a supply chain. Lady V just said \'follow the river, mark what you see.\'"  \n\n2. KWAYOTHÉ\'S WARNING: Only if players seek her out. She\'s found in a tej house or her personal quarter. She\'s not hostile to Lady E — she respects her. But she wants the party to know what they\'re dealing with. "She wins when you succeed. She wins when you fail. She even wins when you walk away. That\'s her gift."\n\n3. SHOPPING: Mostly flavor. Let Dax buy components, let Juju do her thing. Flask can recommend a specific herbalist for jungle-specific supplies.\n\n4. DEPARTURE: End the scene at the docks, dawn. Flask has the canoes ready. Shago appears briefly with a final note from Lady E: "Trust the river. Trust your instincts. Don\'t trust the silence." And a personal card for Skolnit: "You\'ve earned a seat at a table that matters. Come back, and I\'ll pull out the chair." (This is the player card system — deal this as a private card to Skolnit\'s player.)\n\n---\n\nPLAYER CARDS TO DEAL (use the Card Manager):\n- Skolnit: "Lady E\'s Promise" — "You\'ve earned a seat at a table that matters. Come back, and I\'ll pull out the chair." (type: secret)\n- Dax: "The Sky Dock" — A sketch of the aerial structure with coordinates. "If you happen to pass through." (type: clue)\n- Bobby: "The Thin Veil" — "Past the third portage, the echoes walk. Your kind of people." (type: rumor)\n- Juju: "No Strings" — "I redirected your supply line. Here\'s the original source. We\'re even." With actual useful supplier info. (type: item)',
        status: 'upcoming',
      },
    ],
  };

  // Register in the store
  useSessionPlanStore.getState().addSession(session);
  return session;
}
